<template>
  <v-lazy
    class="column-content-width"
    v-model="isActive"
    transition="fade-transition"
    :style="{ 'height: calc(100vh - 170px);': !canEdit }"
    :class="{ 'board-column': canEdit }"
    :options="{
      threshold: 0.5,
    }"
  >
    <v-card
      class="board-column-container d-flex flex-column align-center h-100 overflow-hidden"
      color="blue-grey lighten-3"
      elevation="0"
      :class="canEdit ? 'margin-hover' : 'mt-6'"
    >
      <template v-if="canEdit">
        <div
          class="d-flex align-center justify-center outlined w-100"
          :class="{ 'show-hover': canEdit }"
        >
          <v-icon
            color="blue-grey"
            size="24"
            class="column-drag-control drag_indicator_icon_rotated"
            >drag_indicator</v-icon
          >
        </div>
      </template>
      <div class="d-flex flex-column align-center justify-center flex-grow-1">
        <div class="column-content-width d-flex flex-column flex-grow-1">
          <v-card
            height="40px"
            class="d-flex align-center justify-space-between mt-1 py-2 px-2 mx-1"
            elevation="0"
          >
            <v-skeleton-loader v-if="loading" type="text" width="200px" />
            <p
              class="ma-0 pa-0 subtitle-2 blue-grey--text text--darken-3 board-column-name"
              data-tourid="boards-column-name"
              v-else
            >
              {{ name }}
            </p>

            <v-skeleton-loader v-if="loading" type="text" width="24" />
            <Menu :options="menuOptions" :closeOnContentClick="true">
              <template v-slot:activator="{ on }">
                <div v-on="on">
                  <v-btn
                    v-on="on"
                    icon
                    small
                    data-tourid="boards-column-menu"
                    @click="handleOpenColumnMenu"
                  >
                    <v-icon size="18" color="blue-grey darken-1">
                      mdi-dots-vertical
                    </v-icon>
                  </v-btn>
                </div>
              </template>
            </Menu>
          </v-card>
          <div v-if="showSearch" class="ma-2">
            <SearchInput
              autofocus
              backgroundColor=""
              @change="handleSearch"
              @clear="handleClearSearch"
            />
          </div>
          <div
            class="d-flex justify-space-between align-center py-3 px-2"
            v-if="loadingItems"
          >
            <v-skeleton-loader type="text" width="50" />
            <v-skeleton-loader type="text" width="100" />
          </div>
          <div
            v-if="!loadingItems && totalItems > 0"
            class="d-flex justify-space-between align-center px-2"
            :class="showSearch ? 'pb-3' : 'py-3'"
          >
            <p class="ma-0 pa-0 blue-grey--text text--darken-2 caption ps-1">
              {{ $t('boards.items_count', { count: totalItems }) }}
            </p>

            <div class="d-flex align-center">
              <v-btn
                v-if="showSearchIcon"
                rounded
                icon
                small
                class="me-1"
                color="blue-grey lighten-3"
                data-tourid="boards-search-in-column"
                :class="{ 'blue-grey lighten-1': showSearch }"
                @click="handleSearchClick"
              >
                <v-icon size="18" color="blue-grey darken-1"
                  >mdi-magnify</v-icon
                >
              </v-btn>
              <Menu
                right
                :options="sortOptions"
                :selectedOption="selectedSortOption"
                @click="handleSelectSort"
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    small
                    rounded
                    text
                    data-tourid="boards-sort-column"
                    color="blue-grey"
                    :ripple="false"
                  >
                    <v-icon color="blue-grey darken-1" size="16"
                      >mdi-sort</v-icon
                    >
                    <p class="ma-0 pa-0 blue-grey--text text--darken-2 mx-1">
                      {{ selectedSortOptionText }}
                    </p>
                    <v-icon color="blue-grey darken-1" size="24" class="me-n2"
                      >mdi-chevron-down</v-icon
                    >
                  </v-btn>
                </template>
              </Menu>
            </div>
          </div>

          <EmptyState
            v-if="showEmptyState"
            :showCTA="!showSearch"
            :subtitle="emptyStateSubtitle"
            @edit="$emit('edit', { isEmpty: true })"
          />

          <div v-if="loadingItems" class="mx-2 overflow-hidden">
            <v-skeleton-loader
              v-for="index in columnItems.length + 12"
              class="mb-2"
              type="card"
              height="145px"
              width="328px"
              :key="`skeleton-${index}`"
            />
          </div>
          <virtual-list
            v-if="columnItems.length"
            class="column-content-container mx-2"
            data-key="id"
            :style="
              showSearch
                ? 'height: calc(100vh - 320px)'
                : 'height: calc(100vh - 280px)'
            "
            :extra-props="{ terms }"
            :data-sources="items"
            :data-component="itemComponent"
            :estimate-size="107"
            :item-class="'mb-2'"
            :keeps="25"
          >
            <div slot="footer" class="text-center pb-2">
              <Button
                v-if="columnItems.length < totalItems"
                plain
                :text="$t('common.load_more')"
                :loading="loadingMoreItems"
                :disabled="loadingMoreItems"
                @click="getMoreItems"
              />
            </div>
          </virtual-list>
        </div>
      </div>
    </v-card>
  </v-lazy>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import ContentCard from '@/components/ContentCard.vue'
import EmptyState from '@/containers/BoardView/Column/ColumnEmptyState.vue'

import Menu from '@/components/UI/Menu.vue'
import SearchInput from '@/components/Core/SearchInput.vue'
import Button from '@/components/UI/Button.vue'
import VirtualList from 'vue-virtual-scroll-list'

export default {
  name: 'BoardColumn',

  mixins: [mixin, trackerMixin],

  components: {
    Menu,
    SearchInput,
    EmptyState,
    Button,
    VirtualList,
  },

  props: {
    column: {
      type: Object,
      required: true,
    },
    loading: Boolean,
    canEdit: Boolean,
    refresh: Boolean,
    globalSearchTerm: String,
    columnIndex: { type: Number, default: 0 },
  },

  data() {
    return {
      itemComponent: ContentCard,
      isActive: false,
      terms: '',
      showSearch: false,
      columnItems: [],
      page: 0,
      totalItems: 0,
      loadingItems: true,
      loadingMoreItems: false,
      currentSort: null,
      showSearchIcon: true,
      sortOptions: [
        {
          title: this.$t('common.sort.last_updated'),
          id: 'updatedAt-desc',
        },
        {
          title: this.$t('common.alphabetically'),
          id: 'name-asc',
        },
        {
          title: this.$t('common.highest_priority'),
          id: 'priorityScore-desc',
        },
        {
          title: this.$t('boards.sort.due_date'),
          id: 'dueDate-asc',
        },
        {
          title: this.$t('common.sort.last_created'),
          id: 'createdAt-desc',
        },
      ],
      menuOptions: _.compact([
        !this.canEdit && {
          icon: 'mdi-information-outline',
          id: 'details',
          title: this.$t('common.detail_other'),
          action: () => {
            this.$emit('details')
            this.trackBoardsActions('boards.SelectMenuColumnOption', {
              option: 'details',
            })
          },
        },
        this.canEdit && {
          icon: 'mdi-pencil-outline',
          id: 'edit',
          title: this.$t('common.button.edit'),
          action: () => {
            this.$emit('edit')
            this.trackBoardsActions('boards.SelectMenuColumnOption', {
              option: 'edit',
            })
          },
        },
        {
          icon: 'mdi-restore',
          id: 'refresh',
          title: this.$t('common.button.refresh'),
          action: () => {
            this.resetColumnContent()
            this.trackBoardsActions('boards.SelectMenuColumnOption', {
              option: 'refresh',
            })
          },
        },
        this.canEdit && {
          icon: 'mdi-content-copy',
          id: 'duplicate',
          title: this.$t('common.button.duplicate'),
          action: () => {
            this.$emit('duplicate')
            this.trackBoardsActions('boards.SelectMenuColumnOption', {
              option: 'duplicate',
            })
          },
        },
        this.canEdit && {
          icon: 'mdi-trash-can-outline',
          id: 'delete',
          title: this.$t('common.button.delete'),
          action: () => {
            this.$emit('delete')
            this.trackBoardsActions('boards.SelectMenuColumnOption', {
              option: 'delete',
            })
          },
        },
      ]),
    }
  },

  watch: {
    refresh(value) {
      if (value) {
        this.resetColumnContent()
      }
    },

    async isActive(newValue) {
      if (typeof newValue !== 'boolean') return
      if (newValue && this.globalSearchTerm?.trim()) {
        return this.handleGlobalSearch(this.globalSearchTerm, this.secondsRange)
      }
      if (newValue) {
        this.columnItems = await this.getItems()
      }
    },

    globalSearchTerm(term) {
      if (this.isActive) {
        this.handleGlobalSearch(term, this.secondsRange)
      }
    },

    sort: {
      immediate: true,
      handler() {
        this.currentSort = _.omit(this.sort, '_id')
      },
    },

    async sortAndFilters(newValue, oldValue) {
      const valueIsChanging = !_.isEqual(
        _.omit(newValue, '_id'),
        _.omit(oldValue, '_id')
      )
      if (newValue && oldValue && valueIsChanging) {
        await this.resetColumnContent()
      }
    },
  },

  computed: {
    ...mapGetters(['organizationId', 'organization']),

    name() {
      return this.column?.name
    },

    sort() {
      return this.column?.sort ?? { by: 'createdAt', order: 'desc' }
    },

    initialFilters() {
      return this.column.userFilter?.id
        ? this.column.userFilter?.filters
        : this.column?.filter
    },

    secondsRange() {
      // This function prevents making many calls at the same time
      return (this.columnIndex / 5) * 800
    },

    items() {
      return this.columnItems.map(item => {
        return {
          matchingFields: item.matchingFields,
          ...item.response,
        }
      })
    },

    hasRoutinesFeatureEnabled() {
      return Boolean(this.organization?.featuresEnabled__routines)
    },

    isTasksEnabled() {
      return Boolean(this.organization?.featuresEnabled__tasks)
    },

    selectedSortOption() {
      return `${this.currentSort.by}-${this.currentSort.order}`
    },

    selectedSortOptionText() {
      return (
        this.sortOptions?.find(option => option?.id === this.selectedSortOption)
          ?.title ?? this.$t('common.sort.sort')
      )
    },

    showCards() {
      return !this.loadingItems && this.totalItems > 0
    },

    showEmptyState() {
      return !this.loadingItems && !this.totalItems
    },

    emptyStateSubtitle() {
      return this.showSearch
        ? this.$t('boards.column_empty_state_search_subtitle')
        : this.$t('boards.column_empty_state_subtitle')
    },

    sortAndFilters() {
      return { ...this.initialFilters, ...this.sort }
    },

    enabledFilters() {
      const disabledEntities = []

      if (!this.hasRoutinesFeatureEnabled) {
        disabledEntities.push(
          this.$constants.exploreEntityTypes.routine,
          this.$constants.exploreEntityTypes.finding
        )
      }

      if (!this.isTasksEnabled) {
        disabledEntities.push(this.$constants.exploreEntityTypes.task)
      }

      if (disabledEntities.length) {
        const allTypes = Object.values(this.$constants.exploreEntityTypes)
        const selectedTypesExceptDisabled = (
          this.initialFilters?.types ?? []
        ).filter(entity => !disabledEntities.includes(entity))
        const allTypesExceptDisabled = allTypes.filter(
          entity => !disabledEntities.includes(entity)
        )

        return {
          ...this.initialFilters,
          types: selectedTypesExceptDisabled.length
            ? selectedTypesExceptDisabled
            : allTypesExceptDisabled,
        }
      }

      return this.initialFilters
    },
  },

  methods: {
    handleGlobalSearch(term, time) {
      this.showSearchIcon = !term?.trim()
      this.showSearch = false
      this.loadingItems = true
      setTimeout(() => this.handleSearch(term), time)
    },

    async getMoreItems() {
      const newItems = await this.getItems()
      this.columnItems.push(...newItems)
    },

    async handleSearch(terms) {
      this.terms = terms ?? ''
      await this.resetColumnContent()
      this.trackBoardsActions('boards.SearchContentColumnByTerm', {
        searchTerm: this.terms,
      })
    },

    async handleSearchClick() {
      if (this.showSearch && this.terms) {
        this.terms = ''
        await this.resetColumnContent()
      }

      this.showSearch = !this.showSearch
      this.trackBoardsActions(
        this.showSearch ? 'boards.OpenSearchBar' : 'boards.HideSearchBar'
      )
    },

    async getItems() {
      try {
        if (this.columnItems.length) {
          this.loadingMoreItems = true
        } else {
          this.loadingItems = true
        }
        const filtersWithoutId = _.omit(this.enabledFilters, '_id')
        const response = await api.explore.getContent({
          terms: this.terms,
          organizationId: this.organizationId,
          limit: 12,
          from: this.page * 12,
          sort: this.currentSort,
          filters: { ...filtersWithoutId, isPrivate: false },
          projections: [
            'name',
            'coach',
            'owner',
            'collaborators',
            'organization',
            'status',
            'completedAt',
            'createdAt',
            'createdBy',
            'tags',
            'assignees',
            'totalRevs',
            'startAt',
            'endAt',
            'validations',
            'dueDate',
            'priority',
            'frequency',
            'type',
            'situationTypes',
            'categories',
            'site',
            'description',
            'owner',
            'sites',
            'rewards',
            'KPIs',
            'author',
            'checklist',
            'linkedEntityType',
            'linkedEntityId',
            'beforePhase',
            'afterPhase',
            'relate',
            'expand',
            'problemAnalysis',
            'solutionDescription',
            'image',
            'rewardsText',
          ],
          expansions: [
            'owner',
            'createdBy',
            'tags',
            'assignees',
            'site',
            'sites',
            'author',
            'coach',
            'collaborators',
            'image',
          ],
        })

        this.page = this.page + 1
        this.totalItems = response.data.totalRecordsInData
        return response.data.results
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loadingMoreItems = false
        this.loadingItems = false
      }
    },

    handleSelectSort(sortOption) {
      const [by, order] = sortOption.id.split('-')
      this.currentSort = { order, by }
      this.$emit('updateSort', {
        sort: this.currentSort,
      })
      this.trackBoardsActions('boards.updateSortBy', {
        sortBy: this.currentSort,
      })
    },

    async resetColumnContent() {
      this.page = 0
      this.columnItems = []
      this.columnItems = await this.getItems()
    },

    handleOpenColumnMenu() {
      this.trackBoardsActions('boards.OpenMenuColumnOptions')
    },

    handleClearSearch() {
      this.trackBoardsActions('boards.ClearSearchBar')
    },
  },
}
</script>
<style lang="scss" scoped>
.drag_indicator_icon_rotated {
  transform: rotate(90deg);
}

.board-column-container {
  .column-drag-control {
    cursor: grab;
    visibility: hidden;
  }
  .column-drag-control:active {
    cursor: grabbing !important;
  }

  .column-content-container {
    overflow-y: scroll;
    position: relative;
  }

  &:hover {
    .column-drag-control {
      visibility: visible;
    }
  }

  .column-content-width {
    width: 344px;
  }
}

.board-column-name {
  width: 310px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}
.board-column {
  height: calc(100vh - 170px);
  &:hover {
    height: calc(100vh - 146px);
    .show-hover {
      display: flex !important;
    }
    .margin-hover {
      margin-top: 0px;
    }
  }
}

.margin-hover {
  margin-top: 24px;
}

.show-hover {
  display: none !important;
}
</style>
